import { useEffect, useState } from 'react'
import { api, session } from '../api.js'
import QuestionForm, { slugifier } from '../components/QuestionForm.jsx'
import AccesDemarche from '../components/AccesDemarche.jsx'
import ChoisirRubrique from '../components/ChoisirRubrique.jsx'

export default function Demarches({ ouvrir }) {
  const [liste, setListe] = useState([])
  const [enLigne, setEnLigne] = useState({})
  const [ouverte, setOuverte] = useState(ouvrir?.key || null)
  const [message, setMessage] = useState('')
  const [aSuspendre, setASuspendre] = useState(null)
  const [creation, setCreation] = useState(null)

  const charger = () => {
    api.forms().then(setListe)
    api.formsStatus().then(setEnLigne).catch(() => setEnLigne({}))
  }
  useEffect(() => { charger() }, [])   // eslint-disable-line

  if (ouverte) return <Editeur formKey={ouverte} ongletInitial={ouvrir?.onglet}
                               onRetour={() => { setOuverte(null); charger() }} />

  async function suspendre(f, motif) {
    const r = await api.suspend(f.key, motif)
    setMessage(`« ${f.title} » : ${r.message}`)
    setASuspendre(null)
    charger()
  }
  async function reprendre(f) {
    await api.resume(f.key)
    setMessage(`« ${f.title} » est de nouveau visible sur le portail.`)
    charger()
  }

  return (
    <>
      <h1>Mes démarches</h1>
      <p className="chapo">Ce que voient les habitants. Vous préparez vos changements en brouillon,
        puis vous les mettez en ligne quand vous êtes prêt.</p>
      {message && <p className="succes" role="status">{message}</p>}

      {session.env === 'draft' && (
        <div className="ligne">
          <button className="btn pri" onClick={() => setCreation({ modele: 'vierge' })}>
            Créer une démarche
          </button>
          <span className="aide">Ou piochez dans la bibliothèque : tout y est déjà écrit.</span>
        </div>
      )}

      <div className="grille">
        {liste.map((f) => (
          <article className="fiche" key={f.key}>
            <p className="cat">{f.category}</p>
            <h3>{f.title}</h3>
            <p className="mono petit">{f.questions} questions · circuit {f.workflow_key}</p>
            <div className="ligne">
              {enLigne[f.key]
                ? (enLigne[f.key].suspended
                    ? <span className="suspendue" title={enLigne[f.key].reason}>suspendue</span>
                    : enLigne[f.key].published
                      ? <span className="ok">en ligne</span>
                      : <span className="tag">masquée</span>)
                : <span className="tag">pas encore publiée</span>}
              {f.featured && <span className="tag">mise en avant</span>}
            </div>
            {enLigne[f.key]?.suspended && enLigne[f.key].reason && (
              <p className="motif-suspension">
                {enLigne[f.key].reason}
                {enLigne[f.key].by && <span> — {enLigne[f.key].by}</span>}
              </p>
            )}
            <div className="ligne">
              <button className="btn sm" onClick={() => setOuverte(f.key)}>Modifier</button>
              {session.env === 'draft' && (
                <button className="btn sm" title="Repartir de cette démarche"
                        onClick={() => setCreation({ modele: f.key, source: f.title,
                                                     titre: f.title + ' (copie)',
                                                     categorie: f.category })}>
                  Dupliquer
                </button>
              )}
              {enLigne[f.key] && (enLigne[f.key].suspended
                ? <button className="btn sm" onClick={() => reprendre(f)}>Remettre en ligne</button>
                : <button className="btn sm dgr" onClick={() => setASuspendre(f)}
                          title="Retire la démarche du portail tout de suite, sans publier vos autres travaux">
                    Suspendre
                  </button>)}
            </div>
          </article>
        ))}
        {!liste.length && (
          <div className="carte vide">
            <h2>Aucune démarche pour l'instant</h2>
            <p>Le plus simple est de piocher dans la bibliothèque : tout y est déjà écrit.</p>
          </div>
        )}
      </div>

      {creation && (
        <CreerDemarche etat={creation} onAnnuler={() => setCreation(null)}
                       onCree={(key, msg) => { setCreation(null); setMessage(msg); setOuverte(key) }} />
      )}

      {aSuspendre && (
        <Suspendre demarche={aSuspendre} onAnnuler={() => setASuspendre(null)}
                   onValider={(motif) => suspendre(aSuspendre, motif)} />
      )}
    </>
  )
}

// Créer de zéro ou repartir d'une démarche existante. Deux besoins que la
// bibliothèque seule ne couvrait pas : la demande propre à la commune, et les
// deux démarches presque identiques.
function CreerDemarche({ etat, onCree, onAnnuler }) {
  const [titre, setTitre] = useState(etat.titre || '')
  const [categorie, setCategorie] = useState(etat.categorie || '')
  const [erreur, setErreur] = useState('')

  async function creer() {
    try {
      const r = await api.creerDemarche({ title: titre.trim(), category: categorie,
                                          modele: etat.modele })
      onCree(r.key, r.message)
    } catch (e) { setErreur(e.message) }
  }

  return (
    <div className="fenetre" role="dialog" aria-label="Créer une démarche">
      <div className="fenetre-corps">
        <h2>{etat.source ? `Dupliquer « ${etat.source} »` : 'Nouvelle démarche'}</h2>
        {erreur && <p className="erreur">{erreur}</p>}
        <div className="champ">
          <label htmlFor="c-titre">Comment s'appelle-t-elle ?</label>
          <input id="c-titre" value={titre} autoFocus
                 placeholder="Demande de branchement à l'eau"
                 onChange={(e) => setTitre(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && titre.trim() && creer()} />
          <p className="aide">Le titre que liront les habitants sur le portail.</p>
        </div>
        <ChoisirRubrique id="c-cat" valeur={categorie} onChange={setCategorie} />
        <p className="aide">
          {etat.source
            ? 'Les questions, le circuit et les durées de conservation sont repris. La copie reste hors ligne le temps que vous la modifiiez.'
            : 'Trois questions de départ sont posées, dont les coordonnées : sans elles, vous ne pourriez pas répondre.'}
        </p>
        <div className="ligne separee">
          <div className="pousse" />
          <button className="btn" onClick={onAnnuler}>Annuler</button>
          <button className="btn pri" disabled={!titre.trim()} onClick={creer}>
            Créer et ouvrir
          </button>
        </div>
      </div>
    </div>
  )
}

// Suspendre demande un motif : dans trois semaines, personne ne se souviendra
// si c'était un bug du formulaire ou la fermeture estivale du service.
function Suspendre({ demarche, onValider, onAnnuler }) {
  const [motif, setMotif] = useState('')
  const suggestions = [
    'Erreur dans le formulaire, correction en cours',
    'Service fermé, reprise à la rentrée',
    'Démarche suspendue par décision du conseil',
  ]

  return (
    <div className="fenetre" role="dialog" aria-label="Suspendre la démarche">
      <div className="fenetre-corps">
        <h2>Suspendre « {demarche.title} »</h2>
        <p className="aide">La démarche disparaît du portail immédiatement. Les dossiers déjà
          déposés continuent leur instruction normalement, et elle ne reviendra pas toute seule
          à la prochaine mise en ligne.</p>

        <div className="champ">
          <label htmlFor="motif">Pourquoi ? (recommandé)</label>
          <textarea id="motif" rows={2} value={motif} autoFocus
                    onChange={(e) => setMotif(e.target.value)} />
          <div className="suggestions">
            {suggestions.map((s) => (
              <button key={s} className="jeton discret" onClick={() => setMotif(s)}>{s}</button>
            ))}
          </div>
          <p className="aide">Ce motif est visible de vos collègues, jamais des habitants.</p>
        </div>

        <div className="ligne separee">
          <div className="pousse" />
          <button className="btn" onClick={onAnnuler}>Annuler</button>
          <button className="btn dgr" onClick={() => onValider(motif)}>Suspendre maintenant</button>
        </div>
      </div>
    </div>
  )
}

function Editeur({ formKey, ongletInitial, onRetour }) {
  const [form, setForm] = useState(null)
  const [catalogue, setCatalogue] = useState(null)
  const [usage, setUsage] = useState(null)
  const [services, setServices] = useState([])
  const [onglet, setOnglet] = useState(ongletInitial || 'questions')
  const [questionEditee, setQuestionEditee] = useState(null)
  const [ajout, setAjout] = useState(false)
  const [message, setMessage] = useState('')
  const [erreur, setErreur] = useState('')

  const charger = () => { api.form(formKey).then(setForm) }
  useEffect(() => {
    charger()
    api.fieldCatalog().then(setCatalogue)
    api.formUsage(formKey).then(setUsage).catch(() => setUsage(null))
    api.services().then(setServices).catch(() => setServices([]))
  }, [formKey])   // eslint-disable-line

  if (!form || !catalogue) return <p className="chargement">Chargement…</p>
  const lectureSeule = session.env === 'live'

  async function enregistrer(champs) {
    const avant = form
    setForm({ ...form, ...champs })
    try {
      await api.saveForm(formKey, champs)
      setMessage('Enregistré en brouillon. Mettez votre portail à jour pour que les habitants le voient.')
      setErreur('')
      charger()
    } catch (e) {
      setForm(avant)
      setErreur(e.message)
    }
  }

  // Les règles ne portent que sur des questions déjà posées.
  const vocabulairePour = (varname) => {
    const index = form.fields.findIndex((f) => f.varname === varname)
    return form.fields.slice(0, index < 0 ? form.fields.length : index)
      .filter((f) => f.varname && catalogue.input_types.includes(f.type))
      .map((f) => ({
        var: 'form_var_' + f.varname, label: f.label, type: f.type, options: f.options || [],
        operators: operateurs(f, catalogue),
      }))
  }

  const idUnique = (base) => {
    let nom = base || 'question'
    let n = 2
    while (form.fields.some((f) => f.varname === nom)) nom = `${base}_${n++}`
    return nom
  }

  function sauverQuestion(q) {
    const existe = form.fields.some((f) => f === questionEditee)
    const fields = existe
      ? form.fields.map((f) => (f === questionEditee ? q : f))
      : [...form.fields, q]
    enregistrer({ fields })
    setQuestionEditee(null)
  }
  function supprimerQuestion(q) {
    if (!confirm(`Supprimer « ${q.label} » ?`)) return
    enregistrer({ fields: form.fields.filter((f) => f !== questionEditee) })
    setQuestionEditee(null)
  }
  function deplacer(index, sens) {
    const cible = index + sens
    if (cible < 0 || cible >= form.fields.length) return
    const copie = [...form.fields]
    ;[copie[index], copie[cible]] = [copie[cible], copie[index]]
    enregistrer({ fields: copie })
  }
  function nouvelleQuestion(modele) {
    const saisie = catalogue.input_types.includes(modele.type)
    const libelle = modele.type === 'page' ? 'Nouvelle page'
      : modele.type === 'title' ? 'Titre de section'
      : modele.type === 'comment' ? 'Texte explicatif' : 'Nouvelle question'
    setAjout(false)
    setQuestionEditee({
      type: modele.type, label: libelle, required: false, hint: '', condition: '',
      options: catalogue.with_options.includes(modele.type) ? ['Premier choix', 'Second choix'] : [],
      ...(saisie ? { varname: idUnique(slugifier(libelle)) } : {}),
    })
  }

  return (
    <>
      <button className="retour" onClick={onRetour}>← Mes démarches</button>
      <h1>{form.title}</h1>
      {message && <p className="succes" role="status">{message}</p>}

      {session.env === 'draft' && (
        <div className="ligne">
          <button className="btn pri" onClick={() => setCreation({ modele: 'vierge' })}>
            Créer une démarche
          </button>
          <span className="aide">Ou piochez dans la bibliothèque : tout y est déjà écrit.</span>
        </div>
      )}
      {erreur && <p className="erreur" role="alert">{erreur}</p>}

      {usage?.total > 0 && (
        <p className="avertissement">
          {usage.total} dossier{usage.total > 1 ? 's ont' : ' a'} déjà été déposé
          {usage.total > 1 ? 's' : ''} sur cette démarche. Vos modifications ne valent que pour les
          suivants : les dossiers en cours gardent le formulaire qu'ils ont connu.
        </p>
      )}

      <div className="onglets" role="tablist">
        {[['questions', 'Les questions'], ['acces', 'Qui traite'], ['reglages', 'Réglages'],
          ['apercu', 'Aperçu']].map(
          ([id, label]) => (
            <button key={id} role="tab" aria-selected={onglet === id}
                    className={onglet === id ? 'on' : ''} onClick={() => setOnglet(id)}>{label}</button>
          ))}
      </div>

      {onglet === 'questions' && (
        <>
          <ol className="questions-liste">
            {form.fields.map((f, i) => (
              <li key={i} className={f.type === 'page' ? 'saut' : ''}>
                <span className="type">
                  {catalogue.families.flatMap((x) => x.items).find((x) => x.type === f.type)?.titre
                    || f.type}
                </span>
                <span className="lib">
                  {f.label}
                  {f.required && <span className="req" title="Réponse obligatoire"> *</span>}
                </span>
                {f.when && <span className="regle-resume">{f.when}</span>}
                {!lectureSeule && (
                  <>
                    <span className="fleches">
                      <button aria-label="Monter" disabled={i === 0}
                              onClick={() => deplacer(i, -1)}>▲</button>
                      <button aria-label="Descendre" disabled={i === form.fields.length - 1}
                              onClick={() => deplacer(i, 1)}>▼</button>
                    </span>
                    <button className="btn sm" onClick={() => setQuestionEditee(f)}>Modifier</button>
                  </>
                )}
              </li>
            ))}
            {!form.fields.length && <li className="vide">Ce formulaire ne pose encore aucune question.</li>}
          </ol>

          {!lectureSeule && (
            <button className="btn ajout" onClick={() => setAjout(true)}>Ajouter une question</button>
          )}
        </>
      )}

      {onglet === 'acces' && <AccesDemarche formKey={formKey} />}

      {onglet === 'reglages' && (
        <Reglages form={form} catalogue={catalogue} services={services}
                  lectureSeule={lectureSeule} onEnregistrer={enregistrer} />
      )}

      {onglet === 'apercu' && <Apercu form={form} catalogue={catalogue} />}

      {ajout && (
        <div className="fenetre" role="dialog" aria-label="Ajouter une question">
          <div className="fenetre-corps large">
            <h2>Que voulez-vous demander ?</h2>
            {catalogue.families.map((f) => (
              <div key={f.famille} className="famille">
                <h3>{f.famille}</h3>
                <div className="grille">
                  {f.items.map((it) => (
                    <button className="tuile" key={it.type} onClick={() => nouvelleQuestion(it)}>
                      <strong>{it.titre}</strong>
                      <span>{it.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="ligne separee">
              <div className="pousse" />
              <button className="btn" onClick={() => setAjout(false)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {questionEditee && (
        <QuestionForm question={questionEditee} catalogue={catalogue}
                      vocabulaire={vocabulairePour(questionEditee.varname)}
                      onEnregistrer={sauverQuestion}
                      onSupprimer={form.fields.includes(questionEditee) ? supprimerQuestion : null}
                      onAnnuler={() => setQuestionEditee(null)} />
      )}
    </>
  )
}

function Reglages({ form, catalogue, services, lectureSeule, onEnregistrer }) {
  return (
    <div className="deux">
      <section className="carte">
        <h2>Ce que voit l'habitant</h2>
        <div className="champ">
          <label htmlFor="r-titre">Titre</label>
          <input id="r-titre" defaultValue={form.title} disabled={lectureSeule}
                 onBlur={(e) => e.target.value !== form.title && onEnregistrer({ title: e.target.value })} />
        </div>
        <div className="champ">
          <label htmlFor="r-desc">Explication affichée sur le portail</label>
          <textarea id="r-desc" rows={3} defaultValue={form.description} disabled={lectureSeule}
                    onBlur={(e) => e.target.value !== form.description
                      && onEnregistrer({ description: e.target.value })} />
        </div>
        {lectureSeule
          ? <div className="champ">
              <label htmlFor="r-cat">Rubrique</label>
              <input id="r-cat" value={form.category} disabled />
            </div>
          : <ChoisirRubrique id="r-cat" valeur={form.category}
                             onChange={(v) => v !== form.category && onEnregistrer({ category: v })} />}
        <div className="champ">
          <label htmlFor="r-conf">Message affiché après l'envoi</label>
          <textarea id="r-conf" rows={2} defaultValue={form.confirm} disabled={lectureSeule}
                    onBlur={(e) => e.target.value !== form.confirm
                      && onEnregistrer({ confirm: e.target.value })} />
        </div>
      </section>

      <aside>
        <div className="carte">
          <h2>Sur le portail</h2>
          <div className="champ">
            <label htmlFor="r-pub">Visibilité</label>
            <select id="r-pub" value={form.published ? '1' : '0'} disabled={lectureSeule}
                    onChange={(e) => onEnregistrer({ published: e.target.value === '1' })}>
              <option value="1">Visible sur le portail</option>
              <option value="0">Masquée pour l'instant</option>
            </select>
          </div>
          <label className="case">
            <input type="checkbox" checked={!!form.featured} disabled={lectureSeule}
                   onChange={(e) => onEnregistrer({ featured: e.target.checked })} />
            Mettre en avant en haut de la page d'accueil
          </label>
          <label className="case">
            <input type="checkbox" checked={!!form.shared} disabled={lectureSeule}
                   onChange={(e) => onEnregistrer({ shared: e.target.checked })} />
            Proposer cette démarche aux communes rattachées
          </label>
        </div>

        <div className="carte">
          <h2>Traitement</h2>
          <div className="champ">
            <label htmlFor="r-wf">Circuit appliqué</label>
            <select id="r-wf" value={form.workflow_key} disabled={lectureSeule}
                    onChange={(e) => onEnregistrer({ workflow_key: e.target.value })}>
              {catalogue.workflows.map((w) => (
                <option key={w.key} value={w.key}>{w.name}</option>
              ))}
            </select>
            <p className="aide">Ce qui se passe une fois le dossier déposé.</p>
          </div>
          <div className="champ">
            <label htmlFor="r-ag">Prise de rendez-vous</label>
            <select id="r-ag" value={form.agenda_key || ''} disabled={lectureSeule}
                    onChange={(e) => onEnregistrer({ agenda_key: e.target.value || null })}>
              <option value="">Sans rendez-vous</option>
              {catalogue.agendas.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
          </div>
        </div>
      </aside>
    </div>
  )
}

// Le formulaire tel que l'habitant le verra, page par page.
function Apercu({ form, catalogue }) {
  const pages = []
  let courante = { titre: '', champs: [] }
  for (const f of form.fields) {
    if (f.type === 'page') {
      if (courante.champs.length) pages.push(courante)
      courante = { titre: f.label, champs: [] }
    } else courante.champs.push(f)
  }
  pages.push(courante)

  return (
    <div className="carte apercu-formulaire">
      <p className="aide">Voici ce que voit l'habitant. Les questions soumises à une règle sont
        signalées : elles n'apparaîtront que dans le cas prévu.</p>
      {pages.map((p, i) => (
        <section key={i} className="page-apercu">
          <h3>Étape {i + 1}{p.titre ? ` · ${p.titre}` : ''}</h3>
          {p.champs.map((f, j) => (
            <div className="champ" key={j}>
              {f.type === 'title' ? <h4>{f.label}</h4>
                : f.type === 'comment' ? <p className="explication">{f.hint || f.label}</p>
                : (
                  <>
                    <label>{f.label}{f.required && <span className="req"> *</span>}</label>
                    {f.hint && <p className="aide">{f.hint}</p>}
                    <ChampFactice champ={f} />
                    {f.when && <p className="regle-resume">{f.when}</p>}
                  </>
                )}
            </div>
          ))}
          {!p.champs.length && <p className="aide">Cette étape est vide.</p>}
        </section>
      ))}
    </div>
  )
}

function ChampFactice({ champ }) {
  if (['select', 'radio'].includes(champ.type)) {
    return (
      <select disabled>
        <option>Choisir…</option>
        {(champ.options || []).map((o) => <option key={o}>{o}</option>)}
      </select>
    )
  }
  if (champ.type === 'checkboxes') {
    return (
      <div>
        {(champ.options || []).map((o) => (
          <label className="case" key={o}><input type="checkbox" disabled />{o}</label>
        ))}
      </div>
    )
  }
  if (champ.type === 'checkbox') return <label className="case"><input type="checkbox" disabled />Oui</label>
  if (champ.type === 'text' || champ.type === 'address') return <textarea rows={3} disabled />
  if (champ.type === 'file') return <input type="file" disabled />
  const types = { email: 'email', tel: 'tel', number: 'number', date: 'date' }
  return <input type={types[champ.type] || 'text'} disabled />
}

function operateurs(f, catalogue) {
  if (f.type === 'checkbox') {
    return [{ op: 'oui', label: 'est cochée' }, { op: 'non', label: "n'est pas cochée" }]
  }
  if (f.type === 'number') {
    return [{ op: '==', label: 'est' }, { op: '!=', label: "n'est pas" },
            { op: '>', label: 'est plus grand que' }, { op: '<', label: 'est plus petit que' },
            { op: 'not_empty', label: 'a été rempli' }, { op: 'is_empty', label: "n'a pas été rempli" }]
  }
  if (f.options?.length) {
    return [{ op: '==', label: 'est' }, { op: '!=', label: "n'est pas" },
            { op: 'not_empty', label: 'a été rempli' }, { op: 'is_empty', label: "n'a pas été rempli" }]
  }
  return [{ op: 'not_empty', label: 'a été rempli' }, { op: 'is_empty', label: "n'a pas été rempli" },
          { op: '==', label: 'est' }, { op: 'contains', label: 'contient' }]
}
